/**
 * ResourceBudgetCard Component
 *
 * Collapsible card component for displaying budget information.
 * Uses Radix UI Accordion for smooth expand/collapse animations.
 *
 * Design specs:
 * - Background: #15203C
 * - Border radius: 20px
 * - Padding: 40px
 * - Gap between sections: 19px
 * - Dividers: 1px solid gray
 * - Chevron: rotates 180deg when expanded
 * - Green totals: #13D480, 20px Bold
 * - Red warnings: #F1745B for warning text
 */

import { useState } from "react";
import { ChevronDown, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/class-control";
import { BudgetSummary } from "@/types/gestao-recursos.interface";

interface ResourceBudgetCardProps {
  /** Card title */
  title: string;
  /** Whether the card starts expanded */
  defaultExpanded?: boolean;
  /** Budget data to display (if null, only shows title and total) */
  budgetData?: BudgetSummary | null;
  /** Simple total value for collapsed-only cards */
  totalValue?: number;
  /** Metadata for dates */
  metadata?: {
    iniciadoEm?: string;
    atualizadoEm?: string;
  };
  /** Edit button handler */
  onEdit?: () => void;
  /** Show edit button */
  showEditButton?: boolean;
  /** Show detailed breakdown (false for collapsed-only cards) */
  showDetails?: boolean;
}

export function ResourceBudgetCard({
  title,
  defaultExpanded = false,
  budgetData,
  totalValue,
  metadata,
  onEdit,
  showEditButton = false,
  showDetails = true,
}: ResourceBudgetCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Format currency to Brazilian Real
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  // Format date to Brazilian format
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  };

  // Determine what total to show
  const displayTotal = budgetData?.orcamentoTotal ?? totalValue ?? 0;

  return (
    <div className="bg-[#15203C] rounded-lg p-4">
      <div className="flex flex-col gap-3">
        {/* Header with title and chevron */}
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-[#F6F6F6] flex-1">
            {title}
          </h3>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-white hover:text-[#7846FF] transition-all"
            aria-label={isExpanded ? "Recolher" : "Expandir"}
          >
            <ChevronDown
              size={20}
              className={cn(
                "transition-transform duration-200",
                !isExpanded && "rotate-[-90deg]"
              )}
            />
          </button>
        </div>

        {/* Divider */}
        <div className="h-0 border-t border-gray-700" />

        {/* Content - conditionally rendered based on showDetails and isExpanded */}
        {isExpanded && showDetails && budgetData && (
          <>
            {/* Budget breakdown rows */}
            <BudgetRow
              label="Desenvolvimento do projeto:"
              value={budgetData.desenvolvimento}
            />
            <BudgetRow
              label="Pré-produção:"
              value={budgetData.preProducao}
            />
            <BudgetRow label="Produção:" value={budgetData.producao} />
            <BudgetRow
              label="Pós-produção:"
              value={budgetData.posProducao}
            />
            <BudgetRow
              label="Despesas administrativas associadas ao projeto:"
              value={budgetData.despesasAdministrativas}
            />

            {/* Divider */}
            <div className="h-0 border-t border-gray-700" />

            {/* Subtotal */}
            <div className="flex items-center justify-between gap-[10px]">
              <p className="text-[16px] font-bold text-white">
                Subtotal - Orçamento de Produção Financiável:
              </p>
              <p className="text-lg font-bold text-[#13D480]">
                {formatCurrency(budgetData.subtotalFinanciavel)}
              </p>
            </div>

            {/* Divider */}
            <div className="h-0 border-t border-gray-700" />

            {/* Fees */}
            <BudgetRow
              label={
                <>
                  Taxa de gerenciamento
                  <span className="text-[#F1745B]">
                    {" "}
                    (até 10% do orçamento):
                  </span>
                </>
              }
              value={budgetData.taxaGerenciamento}
              semibold
            />
            <BudgetRow
              label={
                <>
                  Taxa de agenciamento e colocação{" "}
                  <span className="text-[#F1745B]">(não financiável):</span>
                </>
              }
              value={budgetData.taxaAgenciamento}
              semibold
            />

            {/* Divider */}
            <div className="h-0 border-t border-gray-700" />

            {/* Total financeable */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-bold text-white">
                Total de itens financiáveis:
              </p>
              <p className="text-lg font-bold text-[#13D480]">
                {formatCurrency(budgetData.totalFinanciaveis)}
              </p>
            </div>

            {/* Divider */}
            <div className="h-0 border-t border-gray-700" />

            {/* Total budget */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-bold text-white">
                Orçamento total do projeto:
              </p>
              <p className="text-lg font-bold text-[#13D480]">
                {formatCurrency(budgetData.orcamentoTotal)}
              </p>
            </div>

            {/* Divider */}
            <div className="h-0 border-t border-gray-700" />

            {/* Metadata footer with edit button */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-4">
                {metadata?.iniciadoEm && (
                  <p className="text-xs text-[#888888]">
                    Iniciado em: {formatDate(metadata.iniciadoEm)}
                  </p>
                )}
                {metadata?.atualizadoEm && (
                  <p className="text-xs text-[#888888]">
                    Atualizado em: {formatDate(metadata.atualizadoEm)}
                  </p>
                )}
              </div>

              {showEditButton && (
                <Button
                  onClick={onEdit}
                  variant="outline"
                  size="sm"
                  className="border-[#7846FF] text-[#F5F2FF] hover:bg-[#7846FF] hover:text-white"
                >
                  <Pencil size={14} />
                  Editar
                </Button>
              )}
            </div>
          </>
        )}

        {/* Simple total for collapsed cards or non-detailed cards */}
        {isExpanded && !showDetails && (
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-bold text-white">Total:</p>
            <p className="text-lg font-bold text-[#13D480]">
              {formatCurrency(displayTotal)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Budget row component for consistent formatting
 */
interface BudgetRowProps {
  label: React.ReactNode;
  value: number;
  semibold?: boolean;
}

function BudgetRow({ label, value, semibold = false }: BudgetRowProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <div className="flex items-center justify-between gap-2">
      <p
        className={cn(
          "text-sm text-white",
          semibold ? "font-semibold" : "font-normal"
        )}
      >
        {label}
      </p>
      <p
        className={cn(
          "text-sm text-white whitespace-nowrap",
          semibold ? "font-semibold" : "font-normal"
        )}
      >
        {formatCurrency(value)}
      </p>
    </div>
  );
}
